'use client';

import type { Swiper as SwiperCore } from 'swiper';
import type { CuratedLiveSplitpaneWidgetData } from '@weberus/shared-types';
import type { SwiperOptions } from 'swiper/types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useEffect, useMemo, useRef, type FC } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useAtomValue } from 'jotai';
import { clientLogStandardized, YaTrackerImpression } from '@weberus/client-tracker';
import { waitUntilClickTimeout } from '@weberus/utils';
import { FreeMode } from 'swiper/modules';
import { useIsMounted } from '@weberus/ui/hooks/use-is-mounted';
import { pcWidthAtom } from '@/app/_shared/store/device';
import { SwiperNavButton } from '@/app/_components/common';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { PaneItem } from '../pane-item';

export interface PaneAreaProps {
  items: CuratedLiveSplitpaneWidgetData['items'];
  pageNameLog: HomeLogPageNameValue;
  widgetLogMeta: CuratedLiveSplitpaneWidgetData['logMeta'];
}

export const PaneArea: FC<PaneAreaProps> = ({ items, pageNameLog, widgetLogMeta }) => {
  const swiperRef = useRef<SwiperCore | null>(null);
  const { isPcWidth } = useAtomValue(pcWidthAtom);
  const isMounted = useIsMounted();

  const swiperConfig: SwiperOptions = useMemo(() => {
    return {
      spaceBetween: isPcWidth ? 20 : 0,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      slidesPerView: 'auto' as const,
      centeredSlides: !isPcWidth,
      centeredSlidesBounds: !isPcWidth,
      freeMode: {
        enabled: !isPcWidth,
        momentumRatio: 0.7,
        momentumVelocityRatio: 0.8,
      },
    };
  }, [isPcWidth]);

  /** PC 에서 사용하기 위해 5개씩 그룹핑 */
  const groupedItems = useMemo(() => {
    return Array.from({ length: Math.ceil(items.length / 5) }, (_, i) => items.slice(i * 5, i * 5 + 5));
  }, [items]);

  const showNavigation = isPcWidth && groupedItems.length > 1;

  useEffect(() => {
    showNavigation ? swiperRef.current?.slideToLoop(0, 0) : swiperRef.current?.slideTo(0, 0);
  }, [isPcWidth, showNavigation, swiperRef]);

  const onClickHandler = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: CuratedLiveSplitpaneWidgetData['items'][0],
  ) => {
    e.preventDefault();
    await waitUntilClickTimeout(clientLogStandardized('click', pageNameLog, { ...widgetLogMeta, ...item.logMeta }));
    navigateTo(item.action);
  };

  return (
    <div className="relative pc:w-pc-size">
      {isMounted ? (
        <Swiper
          className="!px-16 pc:!px-0"
          {...swiperConfig}
          loop={showNavigation}
          modules={[FreeMode]}
          onSwiper={(swiper: SwiperCore) => {
            swiperRef.current = swiper;
          }}>
          {isPcWidth ? (
            <>
              {groupedItems.map((items, index) => (
                <SwiperSlide key={index} className="flex">
                  <div className="flex w-full gap-20">
                    {items.map((item, idx) => (
                      <YaTrackerImpression
                        key={idx}
                        onImpressed={() => clientLogStandardized('impression', pageNameLog, item.logMeta)}>
                        <PaneItem key={index} {...item} onClick={(e) => onClickHandler(e, item)} />
                      </YaTrackerImpression>
                    ))}
                  </div>
                </SwiperSlide>
              ))}
            </>
          ) : (
            <>
              {items.map((item, index) => (
                <SwiperSlide key={index} className="mr-12 !w-fit last:mr-0 pc:mr-20">
                  <YaTrackerImpression
                    onImpressed={() => clientLogStandardized('impression', pageNameLog, item.logMeta)}>
                    <PaneItem key={index} {...item} onClick={(e) => onClickHandler(e, item)} />
                  </YaTrackerImpression>
                </SwiperSlide>
              ))}
            </>
          )}
        </Swiper>
      ) : (
        /* Fallback */
        <div className="flex h-[360px] overflow-hidden !px-16 pc:!px-0">
          <div className="flex w-full gap-12 pc:gap-20">
            {items.map((item, index) => (
              <PaneItem
                key={index}
                {...item}
                videoPreview={{ url: '', format: 'MP4' }}
                onClick={(e) => onClickHandler(e, item)}
              />
            ))}
          </div>
        </div>
      )}
      {showNavigation && isMounted && <SwiperNavButton swiperRef={swiperRef} />}
    </div>
  );
};
